/**
 * The operator-adjustable numbers and switches that used to be constants.
 *
 * One list, read by both sides: the server clamps what it reads against it,
 * and the Settings page draws its controls from it, so a range or default
 * cannot say one thing on the page and do another on the server. Values live
 * in the settings table under `key`; an unset key means the default.
 */

export type NumberTunable = {
	kind: "number";
	key: string;
	default: number;
	min: number;
	max: number;
	/** Shown after the input. */
	unit: string;
};

export type ToggleTunable = { kind: "toggle"; key: string; default: boolean };

export type ChoiceTunable<T extends string = string> = {
	kind: "choice";
	key: string;
	default: T;
	options: readonly T[];
};

export type Tunable = NumberTunable | ToggleTunable | ChoiceTunable;

const number = (key: string, def: number, min: number, max: number, unit: string): NumberTunable => ({
	kind: "number",
	key,
	default: def,
	min,
	max,
	unit,
});

const toggle = (key: string, def: boolean): ToggleTunable => ({
	kind: "toggle",
	key,
	default: def,
});

export const AUTO_MERGE_MODES = ["off", "title", "title_ai"] as const;
export type AutoMergeMode = (typeof AUTO_MERGE_MODES)[number];

export const TUNABLES = {
	// Alerts
	allEvents: toggle("notifier.all_events.enabled", false),
	priceIncreases: toggle("notifier.price_increase.enabled", false),
	/** Read in minutes by dispatch.ts's dedupeWindowMs, as it always was. */
	repeatWindow: number("notify.dedupe", 10, 0, 1440, "min"),

	// Matching
	autoExtract: toggle("grouping.auto_extract.enabled", false),
	autoExtractPerHour: number("grouping.auto_extract.per_hour", 100, 10, 2000, "listings"),
	autoMerge: {
		kind: "choice",
		key: "grouping.auto_merge",
		default: "off",
		options: AUTO_MERGE_MODES,
	} as ChoiceTunable<AutoMergeMode>,
	/** Dearest over cheapest known price. Scalped sets sell at 5-10x list, so
	 *  this has to allow that; 0 turns the check off. */
	autoMergePriceRatio: number("grouping.auto_merge.max_price_ratio", 10, 0, 100, "×"),

	// Sweeps
	sweepEvery: number("sweep.default_interval_min", 10, 1, 1440, "min"),
	sweepMaxPages: number("sweep.max_pages", 50, 5, 500, "pages"),
	sweepPagePause: number("sweep.page_pause_ms", 500, 0, 5000, "ms"),

	// Watches — the actual gap between checks, before ±25% jitter.
	watchHot: number("watch.hot_s", 45, 30, 600, "s"),
	watchNormal: number("watch.normal_min", 4, 2, 60, "min"),
	watchCold: number("watch.cold_min", 60, 5, 1440, "min"),

	// Buying
	approvalWait: number("buy.approval_timeout_min", 10, 1, 60, "min"),

	// Housekeeping
	/** Also the alert log: its rows cascade-delete with their event. */
	keepEvents: number("retention.events_days", 90, 7, 3650, "days"),
	keepScreenshots: number("retention.screenshots_days", 14, 1, 365, "days"),
} as const;

export type TunableName = keyof typeof TUNABLES;

const BY_KEY = new Map<string, Tunable>(Object.values(TUNABLES).map((t) => [t.key, t as Tunable]));

export function tunableForKey(key: string): Tunable | undefined {
	return BY_KEY.get(key);
}

/**
 * Read a stored string as the tunable's value: a number clamped into range,
 * a boolean, or one of the choices. Anything unreadable is the default, so a
 * hand-edited row can never push a value past the limits the page enforces.
 */
export function parseTunable(t: NumberTunable, raw: string | null | undefined): number;
export function parseTunable(t: ToggleTunable, raw: string | null | undefined): boolean;
export function parseTunable<T extends string>(t: ChoiceTunable<T>, raw: string | null | undefined): T;
export function parseTunable(t: Tunable, raw: string | null | undefined): number | boolean | string;
export function parseTunable(t: Tunable, raw: string | null | undefined): number | boolean | string {
	if (raw == null || raw === "") return t.default;
	if (t.kind === "toggle") return raw === "true" || raw === "1";
	if (t.kind === "choice") return t.options.includes(raw) ? raw : t.default;
	const n = Number(raw);
	if (!Number.isFinite(n)) return t.default;
	return Math.min(t.max, Math.max(t.min, n));
}

/**
 * Check a value on its way in. Returns the string to store, or an error that
 * names the allowed range — a value out of range is refused rather than
 * silently clamped, so the page shows what was actually saved.
 */
export function validateTunable(
	t: Tunable,
	value: unknown,
): { ok: true; value: string } | { ok: false; error: string } {
	if (t.kind === "toggle") {
		if (typeof value === "boolean") return { ok: true, value: String(value) };
		if (value === "true" || value === "false") return { ok: true, value };
		return { ok: false, error: `${t.key} must be true or false` };
	}
	if (t.kind === "choice") {
		if (typeof value === "string" && t.options.includes(value)) {
			return { ok: true, value };
		}
		return { ok: false, error: `${t.key} must be one of ${t.options.join(", ")}` };
	}
	const n = typeof value === "number" ? value : Number(value);
	if (!Number.isFinite(n) || n < t.min || n > t.max) {
		return { ok: false, error: `${t.key} must be between ${t.min} and ${t.max} ${t.unit}` };
	}
	return { ok: true, value: String(n) };
}
